import type { GetDocumentByPathQuery } from '#src/graphql/generated';

import { useMutation } from '@apollo/client/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useEditDocument } from './edit-document-context';
import { normalizeDocumentLinksInEditorJsData } from './editor-document-links';
import {
  type EditorJsData,
  editorJsStableStringForDirtyCompare,
  editorJsStableStringForNewDocPlaceholderCompare,
  normalizeLeadingH1HeaderToDocumentHeading,
  parseEditorJs,
  stringifyEditorJs,
} from './editorjs-types';
import { refetchInTransition } from './refetch-utils';

import { toaster } from '#src/components/ui/toaster';
import { UPSERT_DOCUMENT } from '#src/graphql/mutations/upsert-document';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { formatDocumentName } from '#src/utils/format-document-name';

export type DocumentByPathResult = GetDocumentByPathQuery['documentByPath'];

function getPathFromResult(
  result: DocumentByPathResult,
  fallback: string,
): string {
  return result?.__typename === 'Document'
    ? result.path
    : result?.__typename === 'DocumentNotFound'
      ? (result.requestedPath ?? fallback)
      : fallback;
}

export interface DocumentContextValue {
  document: DocumentByPathResult;
  path: string;
  displayPath: string;
  documentId: string | undefined;
  isNewDocument: boolean;
  /** Muted app header / editor until user edits a new-path placeholder. */
  editorPlaceholderMuted: boolean;
  childrenItems: Array<{ id: string; path: string }>;
  childrenHasMore: boolean;
  editorInitialData: EditorJsData | null;
  lastSavedSerialized: string;
  isDirty: boolean;
  dirtyEditGeneration: number;
  getSerializedDocument: () => string;
  save: (options?: { silent?: boolean }) => Promise<void>;
  saving: boolean;
  saveInFlightRef: MutableRefObject<boolean>;
  revertToSaved: () => void;
  refetch: () => Promise<unknown>;
}

const DocumentContext = createContext<DocumentContextValue | undefined>(
  undefined,
);

export interface DocumentProviderProps {
  document: DocumentByPathResult;
  path: string;
  refetch: () => Promise<unknown>;
  children: ReactNode;
}

export function DocumentProvider({
  document,
  path,
  refetch,
  children,
}: DocumentProviderProps) {
  const navigate = useNavigate();
  const { documentPathRegex } = useAppSettings();
  const { getValue, setValue, flushSave } = useEditDocument();

  const isNewDocument = document?.__typename === 'DocumentNotFound';
  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath = getPathFromResult(document, path);

  const childrenItems = useMemo(
    () =>
      (document?.children?.items as Array<{
        id: string;
        path: string;
      }> | null) ?? [],
    [document?.children?.items],
  );
  const childrenHasMore = useMemo(
    () => document?.children?.pagination?.hasMore ?? false,
    [document?.children?.pagination?.hasMore],
  );

  const documentValue =
    document?.__typename === 'Document' ? document.value : null;

  const [editorInitialData] = useState(() => {
    const parsedRaw = documentValue
      ? (parseEditorJs(documentValue) ?? null)
      : null;
    const parsed = parsedRaw?.blocks?.length
      ? normalizeLeadingH1HeaderToDocumentHeading(parsedRaw)
      : parsedRaw;
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    const base: EditorJsData = parsed?.blocks?.length
      ? parsed
      : (() => {
          const segments = formatDocumentName(displayPath);
          const title =
            segments.length > 0
              ? segments[segments.length - 1]
              : displayPath || 'Untitled';
          return {
            time: Date.now(),
            blocks: [{ type: 'documentHeading', data: { text: title } }],
            version: '2.0.0',
          };
        })();
    return normalizeDocumentLinksInEditorJsData(
      base,
      documentPathRegex,
      origin,
    );
  });

  const newDocPlaceholderBaselineRef = useRef(
    editorJsStableStringForNewDocPlaceholderCompare(editorInitialData),
  );
  const [editorPlaceholderMuted, setEditorPlaceholderMuted] =
    useState(isNewDocument);

  const [lastSavedSerialized, setLastSavedSerialized] = useState(
    stringifyEditorJs(editorInitialData),
  );
  const [dirtyEditGeneration, setDirtyEditGeneration] = useState(0);
  const prevSerializedForAutoSaveRef = useRef(
    editorJsStableStringForDirtyCompare(editorInitialData),
  );

  const [isDirty, setIsDirty] = useState(false);

  const [upsertDocument, { loading: saving }] = useMutation(UPSERT_DOCUMENT);
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    setLastSavedSerialized(stringifyEditorJs(editorInitialData));
  }, [editorInitialData]);

  useEffect(() => {
    prevSerializedForAutoSaveRef.current = editorJsStableStringForDirtyCompare(
      parseEditorJs(lastSavedSerialized) ?? null,
    );
  }, [lastSavedSerialized]);

  const getSerializedDocument = useCallback(() => {
    return stringifyEditorJs(getValue() ?? null);
  }, [getValue]);

  const applyAfterSave = useCallback(
    ({ savedValue, sentValue }: { savedValue: string; sentValue: string }) => {
      const sentData = parseEditorJs(sentValue) ?? null;
      const currentData = getValue() ?? null;
      const sentStable = editorJsStableStringForDirtyCompare(sentData);
      const currentStable = editorJsStableStringForDirtyCompare(currentData);
      if (sentStable === currentStable) {
        const savedData = parseEditorJs(savedValue) ?? null;
        setValue(savedData);
        setIsDirty(false);
      } else {
        setIsDirty(true);
      }
      setLastSavedSerialized(savedValue);
    },
    [getValue, setValue],
  );

  const save = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      try {
        await flushSave();
        const value = getSerializedDocument();
        const { data } = await upsertDocument({
          variables: {
            input: {
              id: documentId ?? path,
              path: isNewDocument ? path : displayPath,
              value,
              format: 'editorjs-v1',
            },
          },
        });

        if (!silent) {
          toaster.create({
            title: 'Success',
            description: isNewDocument
              ? 'Document created successfully'
              : 'Document saved successfully',
          });
        }

        const savedValueString = data?.upsertDocument?.document?.value ?? value;
        applyAfterSave({ savedValue: savedValueString, sentValue: value });

        await refetchInTransition(refetch);

        if (isNewDocument) {
          navigate(`/documents/${path}`, { replace: true });
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [
      applyAfterSave,
      displayPath,
      documentId,
      getSerializedDocument,
      isNewDocument,
      navigate,
      path,
      refetch,
      flushSave,
      upsertDocument,
    ],
  );

  const revertToSaved = useCallback(() => {
    const savedData = parseEditorJs(lastSavedSerialized) ?? null;
    setValue(savedData);
    setIsDirty(false);
  }, [lastSavedSerialized, setValue]);

  useEffect(() => {
    let alive = true;

    const checkForChanges = async () => {
      try {
        await flushSave();
      } catch {
        return;
      }
      if (!alive) return;

      const editorData = getValue() ?? null;
      const currentStable = editorJsStableStringForDirtyCompare(editorData);
      const savedStable = editorJsStableStringForDirtyCompare(
        parseEditorJs(lastSavedSerialized) ?? null,
      );
      const dirty = currentStable !== savedStable;
      setIsDirty(dirty);
      if (currentStable !== prevSerializedForAutoSaveRef.current) {
        prevSerializedForAutoSaveRef.current = currentStable;
        if (dirty) {
          setDirtyEditGeneration((n) => n + 1);
        }
      }

      if (isNewDocument) {
        const stillDefaultPlaceholder =
          editorData == null ||
          editorJsStableStringForNewDocPlaceholderCompare(editorData) ===
            newDocPlaceholderBaselineRef.current;
        setEditorPlaceholderMuted((prev) =>
          prev === stillDefaultPlaceholder ? prev : stillDefaultPlaceholder,
        );
      } else {
        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
      }
    };

    const interval = setInterval(() => void checkForChanges(), 500);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [flushSave, getValue, isNewDocument, lastSavedSerialized]);

  const value = useMemo<DocumentContextValue>(
    () => ({
      document,
      path,
      displayPath,
      documentId,
      isNewDocument,
      editorPlaceholderMuted,
      childrenItems,
      childrenHasMore,
      editorInitialData,
      lastSavedSerialized,
      isDirty,
      dirtyEditGeneration,
      getSerializedDocument,
      save,
      saving,
      saveInFlightRef,
      revertToSaved,
      refetch,
    }),
    [
      childrenHasMore,
      childrenItems,
      dirtyEditGeneration,
      displayPath,
      document,
      documentId,
      editorInitialData,
      editorPlaceholderMuted,
      getSerializedDocument,
      isDirty,
      isNewDocument,
      lastSavedSerialized,
      path,
      refetch,
      revertToSaved,
      save,
      saving,
    ],
  );

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
}

export function useDocumentContext(): DocumentContextValue {
  const ctx = useContext(DocumentContext);
  if (!ctx) {
    throw new Error('useDocumentContext must be used within DocumentProvider');
  }
  return ctx;
}
